import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Inbox, Send, Sparkles } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { api } from '../lib/api';
import { MatchVsCard } from '../components/MatchVsCard';
import { TradeModal } from '../components/TradeModal';
import { useI18n } from '../lib/i18n';
import confetti from 'canvas-confetti';

export function Matches() {
  const { token, user } = useAuth();
  const qc = useQueryClient();
  const { t } = useI18n();
  const [sameCity, setSameCity] = useState(false);
  const [activeMatch, setActiveMatch] = useState<any | null>(null);
  const [sent, setSent] = useState<Set<string>>(new Set());

  const { data, isLoading } = useQuery({
    queryKey: ['matches', sameCity],
    queryFn: () => api.matches(token!, { sameCity, limit: 20 }),
  });
  const { data: col } = useQuery({
    queryKey: ['collection'],
    queryFn: () => api.collection(token!),
  });

  // Outbox para mostrar "ya tenés trade activo con este user"
  const { data: outbox } = useQuery({
    queryKey: ['trades', 'outbox'],
    queryFn: () => api.listTrades('outbox', token!),
  });
  const pendingPartnersOutbox = new Set(
    (outbox?.trades ?? [])
      .filter((t: any) => t.status === 'proposed')
      .map((t: any) => t.receiver_id),
  );

  const matches = data?.matches ?? [];

  function onSent() {
    confetti({
      particleCount: 150, spread: 80, origin: { y: 0.4 },
      colors: ['#c6ff5e', '#5ee0ff', '#ffd166'],
    });
    if (activeMatch) {
      setSent((prev) => new Set(prev).add(activeMatch.user_id));
    }
    setActiveMatch(null);
    qc.invalidateQueries({ queryKey: ['trades'] });
  }

  return (
    <div className="space-y-5">
      <header className="flex items-start justify-between gap-3">
        <div>
          <p className="text-2xs uppercase tracking-widest text-haze">{t('matches.eyebrow')}</p>
          <h1 className="font-display font-extrabold text-3xl">
            {t('matches.title')} <span className="text-gradient">{t('matches.titleAccent')}</span>
          </h1>
        </div>
        <Link
          to="/app/trades"
          className="shrink-0 grid place-items-center h-10 w-10 rounded-full glass hover:bg-graphite/40 transition"
          aria-label={t('matches.myTrades')}
        >
          <Inbox size={18} className="text-haze" />
        </Link>
      </header>

      {/* filter pill */}
      <div className="flex gap-2">
        <button
          onClick={() => setSameCity(false)}
          className={`chip ${!sameCity ? 'border-foam/30 bg-foam/10 text-foam' : ''}`}
        >
          {t('matches.filterAll')}
        </button>
        <button
          onClick={() => setSameCity(true)}
          className={`chip ${sameCity ? 'border-lime/30 bg-lime/10 text-lime' : ''}`}
        >
          {t('matches.filterCity')}
        </button>
      </div>

      {isLoading && (
        <div className="glass rounded-2xl p-8 text-center text-haze">{t('matches.searching')}</div>
      )}
      {!isLoading && matches.length === 0 && (
        <div className="glass rounded-2xl p-8 text-center">
          <p className="text-haze">{t('matches.noMatches')}</p>
          <p className="mt-1 text-xs text-haze">{t('matches.uploadMore')}</p>
        </div>
      )}

      <div className="space-y-4">
        {matches.map((m) => {
          const isSent = sent.has(m.user_id) || pendingPartnersOutbox.has(m.user_id);
          return (
            <div key={m.user_id} className="relative">
              <MatchVsCard
                me={{ displayName: user?.displayName ?? t('matches.you') }}
                them={{ displayName: m.display_name, avatarUrl: m.avatar_url, city: m.city }}
                score={m.score}
                iCanGive={m.iCanGive}
                iCanReceive={m.iCanReceive}
                onPropose={() => !isSent && setActiveMatch(m)}
              />
              {isSent && (
                <div className="absolute top-3 right-3 chip-cyan text-2xs">
                  <Send size={11} className="inline mr-1" /> {t('matches.tradeSent')}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {matches.length > 0 && (
        <Link
          to="/app/trades"
          className="block glass rounded-2xl p-4 text-center text-sm font-semibold text-cyan hover:bg-graphite/40 transition"
        >
          <Sparkles size={14} className="inline mr-1" /> {t('matches.viewTrades')}
        </Link>
      )}

      {/* Modal de propuesta */}
      {activeMatch && col && (
        <TradeModal
          isOpen={!!activeMatch}
          onClose={() => setActiveMatch(null)}
          receiver={{
            id: activeMatch.user_id,
            displayName: activeMatch.display_name,
            city: activeMatch.city,
            avatarUrl: activeMatch.avatar_url,
          }}
          myStickers={col.stickers as any}
          iCanGiveNumbers={activeMatch.iCanGive}
          iCanReceiveNumbers={activeMatch.iCanReceive}
          score={activeMatch.score}
          onSent={onSent}
        />
      )}
    </div>
  );
}
